import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, Sparkles, Trophy, Crown, Flame, Share2, Layers } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shareWinCard } from "@/lib/shareCard";
import { useAudio } from "@/context/GameFeel";
import { VoltSay } from "@/components/Volt";
import { quip } from "@/lib/volt";

export function WvTutorial({ onClose }) {
  return createPortal(
    <div data-testid="wv-tutorial" role="dialog" aria-modal="true" className="fixed inset-0 z-[80] overflow-y-auto bg-[#0a0d14]/95 backdrop-blur-sm">
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-sm glass rounded-3xl border border-emerald-500/30 p-6 sm:p-8 text-center flex flex-col items-center">
          <Sparkles className="w-10 h-10 text-emerald-400 mb-3" />
          <h3 className="font-display text-2xl font-black mb-4">How WordVolt works</h3>
          <ul className="text-left text-sm text-gray-300 space-y-3 mb-6">
            <li className="flex gap-2"><span className="text-emerald-400 font-bold">1.</span><span>You get <b>7 letters</b> — the same for every player.</span></li>
            <li className="flex gap-2"><span className="text-emerald-400 font-bold">2.</span><span>Type real words (3+ letters) and hit <b>Enter</b>. Each valid word banks points.</span></li>
            <li className="flex gap-2"><span className="text-emerald-400 font-bold">3.</span><span><b>Longer words</b> score more — and rare letters <span className="text-amber-300 font-bold">J Q X Z</span> pay a big bonus!</span></li>
            <li className="flex gap-2"><span className="text-emerald-400 font-bold">4.</span><span>Highest total when the timer hits zero <b>wins</b>.</span></li>
          </ul>
          <button data-testid="wv-tutorial-start" onClick={onClose}
            className="w-full px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-bold">Got it — let's play</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function SolTutorial({ onClose }) {
  return (
    <div data-testid="sol-tutorial" className="absolute inset-0 z-20 rounded-3xl bg-[#0a0d14]/97 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center">
      <Layers className="w-10 h-10 text-emerald-400 mb-3" />
      <h3 className="font-display text-2xl font-black mb-4">How Solitaire Sprint works</h3>
      <ul className="text-left text-sm text-gray-300 space-y-3 mb-6 max-w-xs">
        <li className="flex gap-2"><span className="text-emerald-400 font-bold">1.</span> You and your opponent get the <b>exact same 24-card deal</b> — pure skill, no luck.</li>
        <li className="flex gap-2"><span className="text-emerald-400 font-bold">2.</span> Start from the <span className="text-emerald-300 font-bold">active card</span>. Tap any board card that's <b>one rank higher or lower</b>.</li>
        <li className="flex gap-2"><span className="text-emerald-400 font-bold">3.</span> Ranks wrap — an <b>Ace</b> connects to both a <b>2</b> and a <b>King</b>. Each card you tap becomes the new active card.</li>
        <li className="flex gap-2"><span className="text-emerald-400 font-bold">4.</span> Build the <b>longest chain</b> you can before the timer hits zero — longest chain <b>wins</b>.</li>
      </ul>
      <button data-testid="sol-tutorial-start" onClick={onClose}
        className="px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-bold">Got it — let's play</button>
    </div>
  );
}

export function DailyWordVolt({ onClose }) {
  const { refresh } = useAuth();
  const { sfx, react } = useAudio();
  const [voltLine, setVoltLine] = useState(null);
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro, play, done
  const [time, setTime] = useState(60);
  const [words, setWords] = useState([]);
  const [input, setInput] = useState("");
  const [msg, setMsg] = useState(null);
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState([]);
  const [showTut, setShowTut] = useState(() => { try { return !localStorage.getItem("wv_tut_seen"); } catch { return false; } });
  const timerRef = useRef(null);
  const total = words.reduce((s, w) => s + w.points, 0);
  const dismissTut = () => { try { localStorage.setItem("wv_tut_seen", "1"); } catch { /* noop */ } setShowTut(false); };
  const loadBoard = async () => { try { const { data: d } = await api.get("/duels/daily-leaderboard"); setBoard(d.leaderboard); } catch { /* ignore */ } };

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/duels/daily");
        setData(d); setTime(d.duration);
        if (d.played) { setPhase("done"); setResult({ score: d.your_score, rank: d.your_rank, total_players: d.total_players, streak: d.streak, already: true }); loadBoard(); }
      } catch (e) { toast.error(apiErr(e)); onClose(); }
    })();
  }, []);
  useEffect(() => {
    if (phase !== "play" || showTut) return;
    timerRef.current = setInterval(() => setTime((tt) => tt - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, showTut]);
  useEffect(() => { if (time <= 0 && phase === "play") submit(); }, [time]);

  const addWord = async () => {
    const w = input.trim().toUpperCase(); setInput("");
    if (w.length < 3) { setMsg({ ok: false, text: "Min 3 letters" }); return; }
    if (words.some((x) => x.word === w)) { setMsg({ ok: false, text: `${w} already used` }); return; }
    try {
      const { data: d } = await api.post("/duels/daily-check", { word: w });
      if (d.valid) { sfx("correct"); setWords((p) => [{ word: d.word, points: d.points }, ...p]); setMsg({ ok: true, text: `${d.word} +${d.points}` }); }
      else { sfx("wrong"); setMsg({ ok: false, text: `${w}: ${d.reason}` }); }
    } catch { setMsg({ ok: false, text: "Could not check word" }); }
  };
  const submit = async () => {
    clearInterval(timerRef.current); setPhase("done");
    try { const { data: d } = await api.post("/duels/daily-submit", { words: words.map((w) => w.word) }); setResult(d); loadBoard(); refresh(); setVoltLine(react({ outcome: "daily", game: "word_volt", vars: { rank: d.rank, streak: d.streak }, bubble: false })); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const share = () => shareWinCard({
    score: result?.score ?? 0, accent: "#f59e0b",
    subtitle: result?.rank ? `Ranked #${result.rank} of ${result.total_players} today` : "Daily Challenge",
  });

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-lg glass rounded-3xl border border-amber-500/30 p-8 relative" data-testid="daily-modal">
        {phase === "play" && showTut && <WvTutorial onClose={dismissTut} />}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-amber-400"><CalendarDays className="w-5 h-5" /><span className="font-display font-bold">Daily WordVolt</span></div>
          {phase === "play" && <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-amber-300"}`}>{time}s</span>}
        </div>

        {phase === "intro" && data && (
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-4">One puzzle a day — same 7 letters for everyone. You get <b>one</b> attempt. Beat the global board!</p>
            {data.streak > 0 && <p className="text-sm text-orange-300 mb-4 flex items-center justify-center gap-1"><Flame className="w-4 h-4" /> {data.streak}-day streak — keep it alive!</p>}
            <div className="flex justify-center gap-2 mb-6 flex-wrap">
              {data.rack.map((ch, i) => (
                <div key={i} className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-purple-600/20 border-2 border-amber-500/40 flex items-center justify-center text-2xl font-black text-amber-100">{ch}</div>
              ))}
            </div>
            <button data-testid="daily-start" onClick={() => setPhase("play")} className="px-8 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-bold">Start today's challenge</button>
            <button onClick={onClose} className="block mx-auto mt-3 text-xs text-gray-500 hover:text-white">Maybe later</button>
          </div>
        )}

        {phase === "play" && data && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">{words.length} words · <span className="text-amber-300 font-mono">{total} pts</span></p>
            <div className="flex justify-center gap-2 mb-4 flex-wrap">
              {data.rack.map((ch, i) => (
                <div key={i} data-testid={`daily-tile-${i}`} className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-purple-600/20 border-2 border-amber-500/40 flex items-center justify-center text-2xl font-black text-amber-100">{ch}</div>
              ))}
            </div>
            <input data-testid="daily-input" autoFocus value={input}
              onChange={(e) => setInput(e.target.value.replace(/[^a-zA-Z]/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") addWord(); }}
              className="w-56 text-center uppercase tracking-widest bg-black/50 border-2 border-amber-500/40 rounded-xl px-4 py-3 text-2xl font-mono focus:border-amber-500 outline-none mb-2" placeholder="TYPE" />
            <div className="h-5 mb-2">{msg && <span className={`text-sm font-mono ${msg.ok ? "text-emerald-400" : "text-red-400"}`}>{msg.text}</span>}</div>
            <div className="flex gap-2 justify-center mb-3">
              <button data-testid="daily-add" onClick={addWord} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold">Submit word</button>
              <button data-testid="daily-finish" onClick={submit} className="px-5 py-2.5 rounded-xl bg-white/10 border border-white/10 font-semibold">Finish</button>
            </div>
            <div className="flex flex-wrap justify-center gap-1.5 max-h-24 overflow-y-auto hide-scroll">
              {words.map((w) => (<span key={w.word} className="px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs font-mono text-amber-200">{w.word} <span className="text-amber-400">+{w.points}</span></span>))}
            </div>
            <p className="text-xs text-gray-600 mt-3">Rare letters <span className="text-amber-300 font-bold">J Q X Z</span> score a bonus!</p>
          </div>
        )}

        {phase === "done" && (
          <div className="text-center py-2">
            <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <h3 className="font-display text-3xl font-black mb-1">{result?.score ?? 0} pts</h3>
            {result?.already
              ? <p className="text-gray-400 mb-2">You already played today — come back tomorrow!</p>
              : <p className="text-amber-300 mb-2">Ranked #{result?.rank} of {result?.total_players} today</p>}
            {result?.streak > 0 && <p className="text-sm text-orange-300 mb-1 flex items-center justify-center gap-1"><Flame className="w-4 h-4" /> {result.streak}-day streak{result?.reward ? ` · +${result.reward} coins` : ""}</p>}
            {voltLine && <div className="mt-3"><VoltSay testid="volt-daily-result" text={voltLine} mood="cheer" label="Volt · word nerd" size={48} /></div>}
            <button data-testid="daily-share" onClick={share} className="mx-auto my-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold">
              <Share2 className="w-4 h-4" /> Share my score
            </button>
            <div className="text-left max-h-48 overflow-y-auto hide-scroll mb-5">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-amber-400" /> Today's global leaderboard</p>
              {board.map((p, i) => (
                <div key={p.user_id + i} className="flex justify-between text-sm py-1 border-b border-white/5">
                  <span>#{i + 1} {p.name}</span><span className="font-mono text-amber-300">{p.score}</span>
                </div>
              ))}
              {board.length === 0 && <p className="text-gray-600 text-sm">Be the first on the board!</p>}
            </div>
            <button data-testid="daily-close" onClick={onClose} className="px-6 py-3 rounded-xl bg-white/5 border border-white/10">Back</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}


const _solOk = (a, b) => { const d = Math.abs(a - b); return d === 1 || d === 12; };

export function DailySolitaire({ onClose }) {
  const { refresh } = useAuth();
  const { sfx, react } = useAudio();
  const [voltLine, setVoltLine] = useState(null);
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro, play, done
  const [time, setTime] = useState(60);
  const [chain, setChain] = useState([]);
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState([]);
  const timerRef = useRef(null);
  const cur = data?.board ? (chain.length ? data.board.cards[chain[chain.length - 1]].r : data.board.start.r) : null;
  const loadBoard = async () => { try { const { data: d } = await api.get("/duels/daily-solitaire-leaderboard"); setBoard(d.leaderboard); } catch { /* ignore */ } };

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/duels/daily-solitaire");
        setData(d); setTime(d.duration);
        if (d.played) { setPhase("done"); setResult({ score: d.your_score, rank: d.your_rank, total_players: d.total_players, streak: d.streak, already: true }); loadBoard(); }
      } catch (e) { toast.error(apiErr(e)); onClose(); }
    })();
  }, []);
  useEffect(() => {
    if (phase !== "play") return;
    timerRef.current = setInterval(() => setTime((tt) => tt - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);
  useEffect(() => { if (time <= 0 && phase === "play") submit(); }, [time]);

  const tapCard = (i) => {
    if (chain.includes(i)) return;
    if (!_solOk(data.board.cards[i].r, cur)) { sfx("wrong"); return; }
    sfx("correct"); setChain((p) => [...p, i]);
  };
  const submit = async () => {
    clearInterval(timerRef.current); setPhase("done");
    try { const { data: d } = await api.post("/duels/daily-solitaire-submit", { moves: chain }); setResult(d); loadBoard(); refresh(); setVoltLine(react({ outcome: "daily", game: "solitaire_sprint", vars: { rank: d.rank, streak: d.streak }, bubble: false })); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const share = () => shareWinCard({
    score: result?.score ?? 0, accent: "#f59e0b", game: "SOLITAIRE", unit: "CHAIN", name: "Solitaire Sprint",
    subtitle: result?.rank ? `Ranked #${result.rank} of ${result.total_players} today` : "Daily Challenge",
  });

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-lg glass rounded-3xl border border-amber-500/30 p-8 relative" data-testid="daily-solitaire-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-amber-400"><Layers className="w-5 h-5" /><span className="font-display font-bold">Daily Solitaire</span></div>
          {phase === "play" && <span className={`font-mono text-2xl font-black ${time <= 10 ? "text-red-400" : "text-amber-300"}`}>{time}s</span>}
        </div>

        {phase === "intro" && data && (
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-4">One free deal a day — the <b>same 24 cards for everyone</b>. Build the longest chain: tap a card one rank higher or lower than the active card (A wraps to K). You get <b>one</b> attempt!</p>
            {data.streak > 0 && <p className="text-sm text-orange-300 mb-4 flex items-center justify-center gap-1"><Flame className="w-4 h-4" /> {data.streak}-day streak — keep it alive!</p>}
            <div className="mb-4 flex justify-center">
              <div className="w-16 h-20 rounded-xl bg-gradient-to-br from-amber-500 to-purple-600 flex items-center justify-center text-3xl font-black text-white">{data.board.start.label}</div>
            </div>
            <button data-testid="daily-sol-start" onClick={() => setPhase("play")} className="px-8 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-bold">Start today's deal</button>
            <button onClick={onClose} className="block mx-auto mt-3 text-xs text-gray-500 hover:text-white">Maybe later</button>
          </div>
        )}

        {phase === "play" && data && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Chain: <span className="text-amber-300 font-mono">{chain.length}</span> · tap a card ±1 rank from the active card</p>
            <div className="mb-3 flex justify-center">
              <div data-testid="daily-sol-active" className="w-16 h-20 rounded-xl bg-gradient-to-br from-amber-500 to-purple-600 flex items-center justify-center text-3xl font-black text-white">{data.board.cards[chain[chain.length - 1]]?.label ?? data.board.start.label}</div>
            </div>
            <div className="grid grid-cols-6 gap-1.5 mb-4">
              {data.board.cards.map((c, i) => {
                const used = chain.includes(i);
                const playable = !used && _solOk(c.r, cur);
                return (
                  <button key={i} data-testid={`daily-sol-card-${i}`} onClick={() => tapCard(i)} disabled={used || !playable}
                    className={`h-12 rounded-lg text-lg font-black transition-all duration-150 ${used ? "opacity-15 bg-white/5" : playable ? "bg-white/10 border-2 border-amber-500/60 text-amber-100" : "bg-white/5 border border-white/10 text-gray-500"}`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <button data-testid="daily-sol-finish" onClick={submit} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold">Finish</button>
          </div>
        )}

        {phase === "done" && (
          <div className="text-center py-2">
            <Trophy className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <h3 className="font-display text-3xl font-black mb-1">Chain of {result?.score ?? 0}</h3>
            {result?.already
              ? <p className="text-gray-400 mb-2">You already played today — come back tomorrow!</p>
              : <p className="text-amber-300 mb-2">Ranked #{result?.rank} of {result?.total_players} today</p>}
            {result?.streak > 0 && <p className="text-sm text-orange-300 mb-1 flex items-center justify-center gap-1"><Flame className="w-4 h-4" /> {result.streak}-day streak{result?.reward ? ` · +${result.reward} coins` : ""}</p>}
            {voltLine && <div className="mt-3"><VoltSay testid="volt-daily-sol-result" text={voltLine} mood="cheer" label="Volt · card shark" size={48} /></div>}
            <button data-testid="daily-sol-share" onClick={share} className="mx-auto my-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-purple-600 font-semibold">
              <Share2 className="w-4 h-4" /> Share my score
            </button>
            <div className="text-left max-h-48 overflow-y-auto hide-scroll mb-5">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-amber-400" /> Today's global leaderboard</p>
              {board.map((p, i) => (
                <div key={p.user_id + i} className="flex justify-between text-sm py-1 border-b border-white/5">
                  <span>#{i + 1} {p.name}</span><span className="font-mono text-amber-300">{p.score}</span>
                </div>
              ))}
              {board.length === 0 && <p className="text-gray-600 text-sm">Be the first on the board!</p>}
            </div>
            <button data-testid="daily-sol-close" onClick={onClose} className="px-6 py-3 rounded-xl bg-white/5 border border-white/10">Back</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
